import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    ActionIcon,
    Card,
    Group,
    Image,
    Menu,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    IconDownload,
    IconDotsVertical,
    IconEye,
    IconPencil,
    IconTrash,
} from '@tabler/icons-react';

import {
    formatTrainingFileSize,
    getTrainingFileIconSrc,
} from '../../helpers/trainingHelpers';
import { TrainingMaterial } from '../../types/training';
import When from '../common/When';

type Props = {
    material: TrainingMaterial;
    isSuperAdmin: boolean;
    actionLoading?: boolean;
    onView: (material: TrainingMaterial) => void;
    onDownload: (material: TrainingMaterial) => void;
    onEdit?: (material: TrainingMaterial) => void;
    onDelete?: (material: TrainingMaterial) => void;
};

const DESCRIPTION_CLAMP_LINES = 2;

export function TrainingMaterialCard({
    material,
    isSuperAdmin,
    actionLoading = false,
    onView,
    onDownload,
    onEdit,
    onDelete,
}: Props) {
    const extension =
        material.fileName.split('.').pop()?.toUpperCase() || 'Unknown';
    const fileIcon = useMemo(
        () => getTrainingFileIconSrc(material.fileName),
        [material.fileName],
    );
    const { thumbnailUrl } = material;
    const [thumbnailFailed, setThumbnailFailed] = useState(false);

    const description = material.description?.trim() ?? '';
    const hasDescription = description.length > 0;
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [descriptionOverflows, setDescriptionOverflows] = useState(false);
    const descriptionRef = useRef<HTMLParagraphElement>(null);

    useEffect(() => {
        setDescriptionExpanded(false);
        setThumbnailFailed(false);
    }, [material.id, description, material.thumbnailUrl]);

    useEffect(() => {
        const element = descriptionRef.current;
        if (!element || !hasDescription || descriptionExpanded) {
            return undefined;
        }

        const measure = () => {
            setDescriptionOverflows(
                element.scrollHeight > element.clientHeight + 1,
            );
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [description, descriptionExpanded, hasDescription]);

    const showDescriptionToggle =
        hasDescription && (descriptionOverflows || descriptionExpanded);

    const handleViewClick = () => {
        onView(material);
    };

    const handleDownloadClick = () => {
        onDownload(material);
    };

    const handleEditClick = () => {
        onEdit?.(material);
    };

    const handleDeleteClick = () => {
        onDelete?.(material);
    };

    const handleToggleDescription = () => {
        setDescriptionExpanded(current => !current);
    };

    const handleThumbnailError = () => {
        setThumbnailFailed(true);
    };

    const showThumbnail = Boolean(thumbnailUrl) && !thumbnailFailed;

    return (
        <Card
            shadow="sm"
            padding="sm"
            radius="md"
            withBorder
            className="h-full flex flex-col border-gray-200 text-gray-600 transition-all duration-150 hover:border-primary/40 hover:bg-primary/5 hover:text-primary hover:shadow-md"
        >
            <UnstyledButton
                type="button"
                onClick={handleViewClick}
                aria-label={`View ${material.title}`}
                className="relative mb-2 block h-28 w-full overflow-hidden rounded-md bg-primary/5 p-0 cursor-pointer"
            >
                <When
                    condition={showThumbnail}
                    fallback={
                        <div className="flex h-full w-full items-center justify-center">
                            <Image
                                w={32}
                                h={32}
                                src={fileIcon}
                                alt={`${extension} file icon`}
                                fit="contain"
                            />
                        </div>
                    }
                >
                    <Image
                        src={thumbnailUrl ?? ''}
                        alt={`${material.title} thumbnail`}
                        fit="cover"
                        h={112}
                        w="100%"
                        className="h-28 w-full"
                        onError={handleThumbnailError}
                    />
                </When>
            </UnstyledButton>
            <Group
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
                className="mb-2 min-h-[2.25rem]"
            >
                <Group
                    gap="sm"
                    align="flex-start"
                    wrap="nowrap"
                    className="min-w-0"
                >
                    <Image
                        w={28}
                        h={28}
                        src={fileIcon}
                        alt={`${extension} file icon`}
                        fit="contain"
                        className="shrink-0"
                    />
                    <Tooltip
                        label={material.title}
                        multiline
                        maw={320}
                        withArrow
                        openDelay={300}
                    >
                        <UnstyledButton
                            type="button"
                            onClick={handleViewClick}
                            className="min-w-0 text-left"
                        >
                            <Text
                                fw={600}
                                size="sm"
                                lineClamp={2}
                                className="min-w-0 text-primary"
                            >
                                {material.title}
                            </Text>
                        </UnstyledButton>
                    </Tooltip>
                </Group>

                <Group gap={4} wrap="nowrap" className="shrink-0">
                    <Tooltip label="View in browser">
                        <ActionIcon
                            variant="subtle"
                            color="teal"
                            size="sm"
                            loading={actionLoading}
                            onClick={handleViewClick}
                            aria-label="View material"
                        >
                            <IconEye size={16} />
                        </ActionIcon>
                    </Tooltip>

                    <When
                        condition={isSuperAdmin}
                        fallback={
                            <Tooltip label="Download">
                                <ActionIcon
                                    variant="subtle"
                                    color="blue"
                                    size="sm"
                                    loading={actionLoading}
                                    onClick={handleDownloadClick}
                                    aria-label="Download material"
                                >
                                    <IconDownload size={16} />
                                </ActionIcon>
                            </Tooltip>
                        }
                    >
                        <Menu shadow="md" width={160} position="bottom-end">
                            <Menu.Target>
                                <ActionIcon
                                    variant="subtle"
                                    color="primary"
                                    size="sm"
                                    aria-label="Material actions"
                                >
                                    <IconDotsVertical size={16} />
                                </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu.Item
                                    leftSection={<IconPencil size={14} />}
                                    onClick={handleEditClick}
                                >
                                    Edit
                                </Menu.Item>
                                <Menu.Item
                                    leftSection={<IconDownload size={14} />}
                                    onClick={handleDownloadClick}
                                >
                                    Download
                                </Menu.Item>
                                <Menu.Item
                                    color="red"
                                    leftSection={<IconTrash size={14} />}
                                    onClick={handleDeleteClick}
                                >
                                    Delete
                                </Menu.Item>
                            </Menu.Dropdown>
                        </Menu>
                    </When>
                </Group>
            </Group>

            <div className="flex-1 flex flex-col mb-2">
                <Text
                    ref={descriptionRef}
                    size="sm"
                    c="dimmed"
                    lineClamp={
                        descriptionExpanded
                            ? undefined
                            : DESCRIPTION_CLAMP_LINES
                    }
                    className={
                        descriptionExpanded ? undefined : 'min-h-[2.25rem]'
                    }
                >
                    <When
                        condition={hasDescription}
                        fallback="No description provided"
                    >
                        {description}
                    </When>
                </Text>
                <div className="mt-1 min-h-[1.25rem]">
                    <When condition={showDescriptionToggle}>
                        <UnstyledButton
                            type="button"
                            onClick={handleToggleDescription}
                            className="text-sm font-medium text-primary hover:underline"
                        >
                            <When
                                condition={descriptionExpanded}
                                fallback="View more"
                            >
                                View less
                            </When>
                        </UnstyledButton>
                    </When>
                </div>
            </div>

            <div className="mt-auto">
                <div className="mb-2 h-px w-full bg-primary/30" />
                <Text size="xs" c="dimmed">
                    {formatTrainingFileSize(material.fileSize)}
                </Text>
            </div>
        </Card>
    );
}
